import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  Search, 
  FileText, 
  Download, 
  CheckCircle, 
  XCircle, 
  HelpCircle,
  AlertTriangle,
  Calendar,
  ClipboardList,
  Loader2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VolontariNonOccasionali() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [volontari, setVolontari] = useState([]);
  const [statistiche, setStatistiche] = useState({});
  const [search, setSearch] = useState('');
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [filtroRegistro, setFiltroRegistro] = useState('tutti');
  const [anno, setAnno] = useState(new Date().getFullYear().toString());
  const [generandoReport, setGenerandoReport] = useState(false);

  useEffect(() => {
    loadVolontari();
  }, [anno]);

  const loadVolontari = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/volontari-non-occasionali?anno=${anno}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVolontari(data.volontari || []);
        setStatistiche(data.statistiche || { totale: 0, attivi: 0, sospesi: 0, cessati: 0 });
      } else {
        setVolontari([]);
        setStatistiche({ totale: 0, attivi: 0, sospesi: 0, cessati: 0 });
      }
    } catch (error) {
      console.error('Errore:', error);
      setVolontari([]);
      setStatistiche({ totale: 0, attivi: 0, sospesi: 0, cessati: 0 });
    } finally {
      setLoading(false);
    }
  };

  const generaReport = async (formato) => {
    setGenerandoReport(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reports/assicurativo-annuale?anno=${anno}&formato=${formato}`,
        { credentials: 'include' }
      );
      
      if (response.status === 403) {
        toast.error('Funzionalità Premium richiesta');
        return;
      }
      
      if (!response.ok) throw new Error('Errore generazione');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_assicurativo_${anno}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report ${formato.toUpperCase()} generato`);
    } catch (error) {
      toast.error('Errore durante la generazione del report');
    } finally {
      setGenerandoReport(false);
    }
  };

  const filteredVolontari = volontari.filter(v => {
    const matchSearch = !search || 
      `${v.nome} ${v.cognome}`.toLowerCase().includes(search.toLowerCase()) ||
      v.codice_fiscale?.toLowerCase().includes(search.toLowerCase());
    
    const matchStato = filtroStato === 'tutti' || v.stato === filtroStato;
    const matchRegistro = filtroRegistro === 'tutti' || v.presenza_registro_vidimato === filtroRegistro;
    
    return matchSearch && matchStato && matchRegistro;
  });

  const getRegistroIcon = (stato) => {
    switch (stato) {
      case 'si': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'no': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'da_verificare': return <HelpCircle className="w-4 h-4 text-amber-500" />;
      default: return <HelpCircle className="w-4 h-4 text-slate-400" />;
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6" data-testid="volontari-non-occasionali-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Volontari Non Occasionali
          </h1>
          <p className="text-slate-500 mt-1">
            Gestione e report per regolazione premio assicurativo
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => generaReport('csv')}
            disabled={generandoReport}
            data-testid="btn-export-csv"
          >
            {generandoReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            CSV
          </Button>
          <Button 
            onClick={() => generaReport('pdf')}
            disabled={generandoReport}
            className="btn-primary"
            data-testid="btn-export-pdf"
          >
            {generandoReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Report PDF
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-[#0055BB]" />
              <div>
                <p className="text-sm text-slate-500">Totale</p>
                <p className="text-2xl font-bold">{volontari.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm text-slate-500">Attivi</p>
                <p className="text-2xl font-bold text-green-600">{statistiche.attivi || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ClipboardList className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-slate-500">In Registro</p>
                <p className="text-2xl font-bold text-blue-600">{statistiche.in_registro_vidimato || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-sm text-slate-500">Da Verificare</p>
                <p className="text-2xl font-bold text-amber-600">{statistiche.da_verificare || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome o codice fiscale..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <Select value={anno} onValueChange={setAnno}>
              <SelectTrigger className="w-32" data-testid="select-anno">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-36" data-testid="select-stato">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti gli stati</SelectItem>
                <SelectItem value="attivo">Attivi</SelectItem>
                <SelectItem value="cessato">Cessati</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filtroRegistro} onValueChange={setFiltroRegistro}>
              <SelectTrigger className="w-40" data-testid="select-registro">
                <SelectValue placeholder="Registro" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti</SelectItem>
                <SelectItem value="si">In registro</SelectItem>
                <SelectItem value="no">Non in registro</SelectItem>
                <SelectItem value="da_verificare">Da verificare</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
            </div>
          ) : filteredVolontari.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nessun volontario non occasionale trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left">Volontario</th>
                    <th className="px-4 py-3 text-left">Codice Fiscale</th>
                    <th className="px-4 py-3 text-left">Data Inizio</th>
                    <th className="px-4 py-3 text-left">Data Cessazione</th>
                    <th className="px-4 py-3 text-center">Registro</th>
                    <th className="px-4 py-3 text-center">Stato</th>
                    <th className="px-4 py-3 text-center">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVolontari.map((v) => (
                    <tr key={v.volontario_id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{v.cognome} {v.nome}</p>
                          {v.email && <p className="text-xs text-slate-500">{v.email}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{v.codice_fiscale}</td>
                      <td className="px-4 py-3">
                        {v.data_inizio_continuativa || v.data_inizio_attivita || '-'}
                      </td>
                      <td className="px-4 py-3">
                        {v.data_cessazione_continuativa || v.data_cessazione_attivita || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {getRegistroIcon(v.presenza_registro_vidimato)}
                          <span className="text-xs">
                            {v.presenza_registro_vidimato === 'si' ? 'Sì' : 
                             v.presenza_registro_vidimato === 'no' ? 'No' : 
                             v.presenza_registro_vidimato === 'da_verificare' ? 'Verifica' : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={v.stato === 'attivo' ? 'default' : 'secondary'}>
                          {v.stato || 'N/D'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/volontari/${v.volontario_id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-xs text-slate-500 italic">
            <strong>Nota:</strong> Il presente elenco ha finalità gestionale e amministrativa interna e può essere utilizzato come supporto per la regolazione del premio assicurativo. 
            Resta fermo che il riferimento formale dell'ente è costituito dal registro ufficiale adottato e dalla documentazione valida ai fini di legge.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
